import { useStateUrlParamsArray } from "../../../common/hooks/useStateUrlParams";
import { setFirstLetterToLowerCase } from "../../../common/stringOperations";
import { LegalEntityAuditStatus } from "../../../legalEntity/legalEntityAuditTab/LegalEntityAudit.types";
import { RiskLevel } from "../../../legalEntity/legalEntityClassificationTab/LegalEntityClassificationTab.types";
import { useTranslation } from "../../../localization/useTranslation";
import { useDownloadLegalEntityAuditReport } from "../../DowloadLegalEntityAuditReport.hooks";
import { LegalEntityAuditGroupAuditModel } from "../../legalEntityAuditGroupOverview/LegalEntityAuditGroupOverview.types";
import { ManagementReportCompletedAuditsProps } from "./ManagementReportCompletedAudits";
import { EvaluationStatus } from "../../../common/components/questionnaireComponents/evaluateQuestionnaireDialog/EvaluateQuestionnaireDialog.types.ts";
import { filterBySelection, toSelectOptions } from "@common/components/managementReport/managementReportFilters";

export function useManagementReportCompletedAudits(props: ManagementReportCompletedAuditsProps) {
    const { translateString, translateEvaluationEnum } = useTranslation();

    const [searchedRiskLevel, setSearchedRiskLevel] = useStateUrlParamsArray("riskLevel", []);
    const [searchedAuditScore, setSearchedAuditScore] = useStateUrlParamsArray("auditScore", []);
    const [searchedAuditEvaluation, setSearchedAuditEvaluation] = useStateUrlParamsArray("auditEvaluation", []);

    const { onDownloadReportAsPdf, getReportPdfUrl, isDownloadingPdf } = useDownloadLegalEntityAuditReport();

    const audits: Array<LegalEntityAuditGroupAuditModel> = props.audits ?? [];

    // Risk level and evaluation only exist once an audit is submitted, so their options come from the submitted rows
    // while the score options cover every row.
    const submittedAudits = audits.filter((audit) => audit.status === LegalEntityAuditStatus.Submitted);

    const riskLevelFilterOptions = toSelectOptions(submittedAudits.map((audit) => toRiskLevelOption(audit.riskLevel)));
    const auditScoreFilterOptions = toSelectOptions(audits.map((audit) => ({ id: audit.score.toString(), name: `${audit.score} %` })));
    const auditEvaluationFilterOptions = toSelectOptions(submittedAudits.map((audit) => toEvaluationOption(audit.evaluationStatus)));

    let filteredRows = filterBySelection(audits, searchedRiskLevel, (row) => row.riskLevel?.toString());
    filteredRows = filterBySelection(filteredRows, searchedAuditScore, (row) => row.score.toString());
    filteredRows = filterBySelection(filteredRows, searchedAuditEvaluation, (row) => row.evaluationStatus?.toString());

    function toRiskLevelOption(riskLevel: RiskLevel | undefined) {
        if (!riskLevel) {
            return undefined;
        }

        return { id: riskLevel.toString(), name: translateString(setFirstLetterToLowerCase(RiskLevel[riskLevel])) };
    }

    function toEvaluationOption(evaluationStatus: EvaluationStatus | undefined) {
        if (!evaluationStatus) {
            return undefined;
        }

        return { id: evaluationStatus.toString(), name: translateEvaluationEnum(evaluationStatus.toString()) };
    }

    return {
        rows: filteredRows,
        searchedRiskLevel,
        setSearchedRiskLevel,
        onDownloadReportAsPdf,
        getReportPdfUrl,
        isDownloadingPdf,
        riskLevelFilterOptions,
        searchedAuditScore,
        setSearchedAuditScore,
        auditScoreFilterOptions,
        searchedAuditEvaluation,
        setSearchedAuditEvaluation,
        auditEvaluationFilterOptions,
    };
}
